import React, { useState, useEffect, useId } from 'react';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import * as fmt from '../../lib/format';

export default function JiraConfigTab() {
  const uid = useId();
  const { user } = useAuth();
  const { showToast } = useToast();
  const notify = (msg: string) =>
    showToast(msg.startsWith('❌') ? 'error' : 'success', msg.replace(/^[✅❌]\s*/, ''));

  const [jiraBaseUrl, setJiraBaseUrl] = useState('');
  const [jiraEmail, setJiraEmail] = useState('');
  const [jiraToken, setJiraToken] = useState('');
  const [isSavingJira, setIsSavingJira] = useState(false);
  const [isTestingJira, setIsTestingJira] = useState(false);

  // Prefill JIRA credentials on mount (admin only)
  useEffect(() => {
    const loadJiraConfig = async () => {
      try {
        const resp = await apiService.getJiraCredentials();
        if (resp.success && resp.data) {
          setJiraBaseUrl(resp.data.siteBaseUrl || '');
          setJiraEmail(resp.data.email || '');
          // Don't prefill token for security; leave blank
        }
      } catch (e) {
        // ignore if no credentials yet
      }
    };
    if ((user?.role === 'admin')) {
      loadJiraConfig();
    }
  }, [user]);

  const handleSaveJiraConfig = async () => {
    if (!(user?.role === 'admin')) return;
    setIsSavingJira(true);
    try {
      if (!jiraBaseUrl || !jiraEmail || !jiraToken) {
        notify('❌ Please fill Base URL, Email and API token');
        return;
      }
      const resp = await apiService.updateJiraCredentials({
        siteBaseUrl: jiraBaseUrl,
        accessToken: jiraToken,
        email: jiraEmail
      });
      if (resp.success) {
        notify('✅ Jira credentials saved successfully');
        setJiraToken(''); // clear token field after save
      } else {
        notify(`❌ ${resp.error || 'Failed to save credentials'}`);
      }
    } catch (e: any) {
      notify(`❌ ${e.response?.data?.error || e.message || 'Save failed'}`);
    } finally {
      setIsSavingJira(false);
    }
  };

  const handleTestJiraConnection = async () => {
    setIsTestingJira(true);
    try {
      if (!jiraBaseUrl || !jiraToken) {
        notify('❌ Base URL and API token are required for test');
        return;
      }
      const params = { siteBaseUrl: jiraBaseUrl, accessToken: jiraToken, email: jiraEmail } as any;
      const resp = await apiService.get('/test-jira', { params });
      const data = resp.data;
      if (data?.success) {
        notify(`✅ Connection OK. totalIssues=${data.data?.totalIssues ?? fmt.MISSING}`);
      } else {
        notify(`❌ ${data?.error || 'Jira test failed'}`);
      }
    } catch (e: any) {
      notify(`❌ ${e.response?.data?.details || e.message || 'Jira test failed'}`);
    } finally {
      setIsTestingJira(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Jira configuration</h3>
        <p className="text-sm text-gray-600">Configure your Jira connection settings</p>
      </div>

      {!(user?.role === 'admin') ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            ⚠️ Only administrators can configure Jira settings.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* JIRA URL Configuration */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Jira instance URL</h4>
            <div className="space-y-2">
              <label htmlFor={`${uid}-1`} className="block text-xs text-gray-700">Jira base URL</label>
              <input
                id={`${uid}-1`}
                type="url"
                placeholder="https://your-domain.atlassian.net"
                value={jiraBaseUrl}
                onChange={(e) => setJiraBaseUrl(e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500">Enter your Jira instance URL (e.g., https://company.atlassian.net)</p>
            </div>
          </div>

          {/* API credentials */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">API credentials</h4>
            <div className="space-y-3">
              <div>
                <label htmlFor={`${uid}-2`} className="block text-xs text-gray-700 mb-1">Email address</label>
                <input
                  id={`${uid}-2`}
                  type="email"
                  placeholder="your-email@company.com"
                  value={jiraEmail}
                  onChange={(e) => setJiraEmail(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor={`${uid}-3`} className="block text-xs text-gray-700 mb-1">API token</label>
                <input
                  id={`${uid}-3`}
                  type="password"
                  placeholder="Enter your Jira API token"
                  value={jiraToken}
                  onChange={(e) => setJiraToken(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Generate an API token from your Jira account settings
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col space-y-2 pt-2">
            <div className="flex space-x-3">
              <button
                onClick={handleSaveJiraConfig}
                disabled={!(user?.role === 'admin') || isSavingJira}
                className="px-4 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isSavingJira ? 'Saving...' : 'Save Configuration'}
              </button>
              <button
                onClick={handleTestJiraConnection}
                disabled={isTestingJira}
                className="px-4 py-2 bg-gray-200 text-gray-700 text-xs rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                {isTestingJira ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
